/**
 * Shared suppression rules for ad-platform pixels (Reddit + Google Ads).
 *
 * Why this exists: ad pixels in the app were polluting conversion data
 * with self-traffic — every dev session, every preview deploy, every
 * paying customer's daily login was firing Lead/PageVisit events.
 * Reddit's optimization algo treats those as "real conversions" and
 * trains its bidding model on them, which means our budget gets spent
 * showing ads to people who look like Daniel-testing-the-signup-view
 * instead of actual ICP prospects.
 *
 * The fix: every track* call passes through shouldSuppress() first.
 * Returns true (skip the event) when ANY of:
 *
 *   1. Hostname isn't trailercast.cloud (the production app). Catches
 *      localhost, *.vercel.app preview deploys, any non-prod domain.
 *      Marketing site (trailercast.io) doesn't import this — it has
 *      its own pixel install in components/Analytics.tsx.
 *   2. The visitor is logged in (has trailercst_token in localStorage).
 *      Logged-in users are existing customers, not converting prospects.
 *      A paying customer reloading the dashboard 50× a day shouldn't
 *      train the bidding model.
 *   3. ?nopixel=1 was ever passed in the URL. Sets a sticky localStorage
 *      flag so the operator can run signup flows on PROD without
 *      polluting data — once flagged, all future sessions on the same
 *      browser also suppress, until manually cleared.
 *
 * Escape hatch: clear localStorage.trailercast_nopixel to re-enable.
 */

const NOPIXEL_FLAG = 'trailercast_nopixel';
const PROD_HOSTNAME = 'trailercast.cloud';

/**
 * Persist the ?nopixel=1 flag once, on first sight. Idempotent — safe
 * to call on every boot. Operator clears via DevTools localStorage.
 */
function persistNopixelFromUrl() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('nopixel') === '1') {
      window.localStorage.setItem(NOPIXEL_FLAG, '1');
    }
  } catch (_) { /* localStorage may be blocked (Safari ITP, private mode) */ }
}

/**
 * Returns true when ad pixel events should be suppressed for this
 * session. Cheap; safe to call on every track*() invocation. Reads
 * are cached in module scope so the second call doesn't re-parse
 * the URL or re-hit localStorage.
 */
let cached = null;
function shouldSuppress() {
  if (cached !== null) return cached;
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    // SSR / non-browser — suppress to be safe (the pixel needs the DOM
    // to fire anyway).
    cached = true;
    return cached;
  }

  // 1. Persist the URL flag if present, then read whatever's there.
  persistNopixelFromUrl();

  let flagged = false;
  let token = null;
  try {
    flagged = window.localStorage.getItem(NOPIXEL_FLAG) === '1';
    token = window.localStorage.getItem('trailercst_token');
  } catch (_) { /* localStorage blocked → treat as no flag, no token */ }

  if (flagged) {
    cached = true;
    return cached;
  }

  // 2. Logged-in users → existing customers, not prospects converting.
  if (token) {
    cached = true;
    return cached;
  }

  // 3. Hostname guard. Only the production app (trailercast.cloud)
  //    fires ad-platform events. Localhost, preview deploys, and any
  //    other domain are suppressed.
  //
  //    Strip a leading "www." before comparing — Vercel serves the
  //    canonical app at https://www.trailercast.cloud (with www) for
  //    real visitors, while internal references and the docs treat
  //    "trailercast.cloud" as the canonical name. Without this
  //    normalization, every legitimate visitor would be suppressed
  //    because their hostname is `www.trailercast.cloud`, not
  //    `trailercast.cloud`. (Found 2026-05-05 while debugging why
  //    gtag.js wasn't loading on production — the env vars were
  //    fine; the suppression was over-eager.)
  const rawHost = (window.location.hostname || '').toLowerCase();
  const host = rawHost.replace(/^www\./, '');
  if (host !== PROD_HOSTNAME) {
    cached = true;
    return cached;
  }

  cached = false;
  return cached;
}

/**
 * Force a re-check on the next call. Used after login/logout state
 * changes — a user logging out should NOT suddenly start firing
 * pixel events on the same page (we still gate on hostname), but a
 * user logging in MUST start being suppressed even if cached said
 * "fire" earlier in the session.
 */
function reset() {
  cached = null;
}

export { shouldSuppress, reset };
export default { shouldSuppress, reset };
